/**
 * Parses an `async_tasks.params` JSON-stringified positional-args array and returns
 * its first element. Backend tasks store their arguments as
 * `JSON.stringify([arg1, arg2, ...])` and most callers reach for `arg1` only.
 *
 * Returns `null` for missing or malformed `params` so callers don't repeat the
 * same try/catch in every consumer.
 *
 * For positional access beyond the first arg, use `parseTaskParamsArray`.
 */
export function parseTaskParams<T = unknown>(task: { params?: string }): T | null {
    try {
        const [first] = JSON.parse(task.params || "[]");
        return (first as T) ?? null;
    } catch {
        return null;
    }
}

/**
 * Parses an `async_tasks.params` JSON-stringified positional-args array and returns
 * the full tuple. Use this when you need positional args beyond the first — e.g.
 * `const [, projectId, payload] = parseTaskParamsArray<[unknown, string, Foo]>(task) ?? []`.
 *
 * Returns `null` for missing or malformed `params`.
 */
export function parseTaskParamsArray<T extends unknown[] = unknown[]>(task: {
    params?: string;
}): T | null {
    try {
        return JSON.parse(task.params || "[]") as T;
    } catch {
        return null;
    }
}
